import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { API } from "aws-amplify";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { updateLLMProjectSummaryEmail } from "../dataaccess/updateProjectSummaryEmail";
import { useQueryClient } from "@tanstack/react-query";
dayjs.extend(utc);

const SendSummaryEmailButton = ({
  project,
  user,
}: {
  project: any;
  user: any;
}) => {
  const [open, setOpen] = useState(false);

  const [selectedDays, setSelectedDays] = useState(7);
  const [emailAddress, setEmailAddress] = useState(user.email);
  const [working, setWorking] = useState(false);
  const [summaryEmailPeriod, setSummaryEmailPeriod] = useState(0);
  const [startingDate, setStartingDate] = React.useState<Dayjs | null>(
    dayjs().add(1, "day").startOf("day")
  );
  const queryClient = useQueryClient();
  const hasScheduledSummaryEmail = (project?.summaryEmailPeriod ?? 0) > 0;

  useEffect(() => {
    if (summaryEmailPeriod > 0 && (project?.summaryEmailPeriod ?? 0) > 0) {
      setStartingDate(
        dayjs(project?.lastSummaryEmailSentAt).add(summaryEmailPeriod, "day")
      );
    }
  }, [project, summaryEmailPeriod]);

  const handleOpen = () => {
    setWorking(false);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedDays(7);
    setEmailAddress(user.email);
    setSummaryEmailPeriod(0);
    setStartingDate(dayjs().add(1, "day").startOf("day"));
    setWorking(false);
    setOpen(false);
  };

  const handleSend = async () => {
    if (!project?.id) return;

    if (summaryEmailPeriod > 0) {
      try {
        setWorking(true);

        await updateLLMProjectSummaryEmail({
          id: project.id,
          summaryEmailPeriod: summaryEmailPeriod,
          summaryEmailAddresses: emailAddress,
          lastSummaryEmailSentAt: startingDate
            ?.subtract(summaryEmailPeriod, "day")
            .utc()
            .toISOString(),
        });

        queryClient.invalidateQueries({ queryKey: ["projects"] });

        handleClose();
      } catch (error) {
        console.error("Failed to schedule summary email:", error);
        handleClose();
      }
    } else {
      try {
        setWorking(true);
        await API.post("eventapi", "/sendSummaryEmail", {
          body: {
            projectId: project.id,
            days: selectedDays,
            emails: emailAddress,
          },
        });

        handleClose();      } catch (error) {
        console.error("Failed to send summary email:", error);
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as any;
          console.error("Error response:", axiosError.response?.data);
          console.error("Error status:", axiosError.response?.status);
        }
        setWorking(false); // Don't close dialog on error
      }
    }
  };

  const handleTurnOffSchedule = async () => {
    if (!project?.id) return;

    try {
      setWorking(true);
      await updateLLMProjectSummaryEmail({
        id: project.id,
        summaryEmailPeriod: 0,
        summaryEmailAddresses: "",
      });

      queryClient.invalidateQueries({ queryKey: ["projects"] });
      handleClose();
    } catch (error) {
      console.error("Failed to turn off scheduled summary email:", error);
      setWorking(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Button variant="text" onClick={handleOpen}>
        Send or Schedule Summary Email
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Send or Schedule Summary Email</DialogTitle>
        <DialogContent>
          {hasScheduledSummaryEmail && (
            <Alert
              severity="info"
              sx={{
                marginBottom: 2,
              }}
            >
              This project has a scheduled summary email every{" "}
              {project?.summaryEmailPeriod}
              {project?.summaryEmailPeriod === 1 ? " day" : " days"}.
            </Alert>
          )}
          {summaryEmailPeriod > 0 && hasScheduledSummaryEmail && (
            <Alert
              severity="warning"
              sx={{
                marginBottom: 2,
                backgroundColor: "#fff3cd",
                color: "#856404",
                "& .MuiAlert-icon": {
                  color: "#856404",
                },
              }}
            >
              This project already has a scheduled email every{" "}
              {project?.summaryEmailPeriod}
              {project?.summaryEmailPeriod === 1 ? " day" : " days"}. The new
              schedule will replace the existing one.
            </Alert>
          )}
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="select-schedule-label">
              One off or Schedule
            </InputLabel>
            <Select
              labelId="select-schedule-label"
              id="select-schedule"
              value={summaryEmailPeriod}
              label="One off or Schedule"
              onChange={(e) => setSummaryEmailPeriod(Number(e.target.value))}
            >
              <MenuItem value={0}>One off</MenuItem>
              <MenuItem value={1}>Scheduled: Every Day</MenuItem>
              <MenuItem value={7}>Scheduled: Every Week</MenuItem>
            </Select>
          </FormControl>

          {summaryEmailPeriod > 0 && (
            <FormControl fullWidth margin="normal" size="small">
              <DateTimePicker
                label="Send first email at"
                value={startingDate as any}
                onChange={(newValue) => setStartingDate(newValue)}
              />
            </FormControl>
          )}

          {summaryEmailPeriod === 0 && (
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel id="select-days-label">
                For the most recent days
              </InputLabel>
              <Select
                labelId="select-days-label"
                id="select-days"
                value={selectedDays}
                label="For the most recent days"
                onChange={(e) => setSelectedDays(Number(e.target.value))}
              >
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={7}>7</MenuItem>
                <MenuItem value={14}>14</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            margin="normal"
            size="small"
            label="Email address(es) - comma separated"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {hasScheduledSummaryEmail && (
            <Button
              disabled={working}
              color="warning"
              onClick={handleTurnOffSchedule}
            >
              Turn off scheduled email
            </Button>
          )}
          <Button disabled={working} variant="contained" onClick={handleSend}>
            {summaryEmailPeriod > 0 ? "Schedule" : "Send"} summary email
          </Button>
          <Typography variant="caption" color="textSecondary">
            {working ? "Sending..." : ""}
          </Typography>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SendSummaryEmailButton;
